import { Fragment, useMemo, useState, type ReactNode } from 'react'
import {
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  ChevronUp,
  Copy,
  Plus,
  Trash2,
} from 'lucide-react'
import {
  buildNetQuantityValue,
  deriveCbp7501LineAutomation,
} from '@/features/case-workspace/cbp7501LineAutomation'
import {
  CBP_7501_MANIFEST_QUANTITY_UNIT_OPTIONS,
} from '@/features/case-workspace/cbp7501'
import {
  buildCbp7501FieldStateEngine,
  type Cbp7501DynamicFieldState,
  type Cbp7501FieldStateEngineResult,
} from '@/features/case-workspace/cbp7501FieldState'
import { DOCUMENT_DEFINITIONS } from '@/features/case-workspace/schemas'
import type {
  DocumentFieldDefinition,
  DocumentWorkspaceFocusTarget,
  StructuredDocumentRecord,
} from '@/features/case-workspace/types'
import {
  FieldControl,
  InlineHelp,
  OptionalSectionNote,
  SectionHeading,
  StatusPill,
  toneForRecommendationState,
} from '@/features/case-workspace/WorkspaceShared'
import { canMarkDocumentComplete, canMarkSectionComplete, isFieldVisible, summarizeSectionStatus } from '@/features/case-workspace/utils'

type CBP7501EditorProps = {
  record: StructuredDocumentRecord
  focusTarget: DocumentWorkspaceFocusTarget | null
  busy: boolean
  saveMessage: string | null
  onFieldChange: (recordId: string, fieldKey: string, nextValue: string) => void
  onFieldNoteChange: (recordId: string, fieldKey: string, note: string) => void
  onSetFieldOverride: (recordId: string, fieldKey: string, enabled: boolean, reason: string) => void
  onSectionNoteChange: (recordId: string, sectionId: string, note: string) => void
  onMarkSectionComplete: (recordId: string, sectionId: string) => void
  onLineItemFieldChange: (recordId: string, lineItemId: string, fieldKey: string, nextValue: string) => void
  onLineItemNoteChange: (recordId: string, lineItemId: string, note: string) => void
  onConfirmLineItemGroup: (recordId: string, lineItemId: string, fieldKeys: string[]) => void
  onAddLineItem: (recordId: string) => void
  onDuplicateLineItem: (recordId: string, lineItemId: string) => void
  onRemoveLineItem: (recordId: string, lineItemId: string) => void
  onMoveLineItem: (recordId: string, lineItemId: string, direction: 'up' | 'down') => void
  onApplyRecommendation: (recordId: string, recommendationId: string) => void
  onConfirmFieldGroup: (recordId: string, fieldKeys: string[]) => void
  onAutoSave: (recordId: string, options?: { auto?: boolean }) => Promise<void>
  onMarkComplete: (recordId: string) => void
}

function toneForSectionStatus(status: ReturnType<typeof summarizeSectionStatus>) {
  switch (status) {
    case 'complete':
      return 'success'
    case 'needs_review':
      return 'warning'
    case 'validation_error':
      return 'error'
    case 'in_progress':
      return 'info'
    default:
      return 'neutral'
  }
}

function labelForSectionStatus(status: ReturnType<typeof summarizeSectionStatus>) {
  switch (status) {
    case 'not_started':
      return 'Not Started'
    case 'in_progress':
      return 'In Progress'
    case 'needs_review':
      return 'Needs Review'
    case 'complete':
      return 'Complete'
    case 'validation_error':
      return 'Validation Error'
  }
}

function labelForFieldReviewStatus(status?: StructuredDocumentRecord['fields'][string]['reviewStatus']) {
  switch (status) {
    case 'extracted':
      return 'Extracted'
    case 'manually_entered':
      return 'Manually entered'
    case 'edited_after_extraction':
      return 'Edited after extraction'
    case 'agent_confirmed':
      return 'Agent confirmed'
    case 'unresolved_issue':
      return 'Unresolved issue'
    default:
      return null
  }
}

function StaticCheckboxIndicator({
  checked = true,
  dimmed = false,
}: {
  checked?: boolean
  dimmed?: boolean
}) {
  return (
    <span
      aria-hidden="true"
      className={`inline-flex h-4 w-4 items-center justify-center rounded border text-[11px] font-bold leading-none select-none ${
        checked
          ? dimmed
            ? 'border-neutral-300 bg-neutral-100 text-neutral-400'
            : 'border-primary/40 bg-primary/10 text-primary'
          : 'border-neutral-300 bg-white text-transparent'
      }`}
    >
      ✓
    </span>
  )
}

function buildFieldHelpText({
  helpText,
  origin,
  enumSource,
  exampleValue,
}: {
  helpText?: string
  origin?: StructuredDocumentRecord['fields'][string]['origin']
  enumSource?: string
  exampleValue?: string
}) {
  const parts = [
    helpText,
    exampleValue ? `Example: ${exampleValue}.` : null,
    origin ? `Current origin: ${origin.replace(/_/g, ' ')}.` : null,
    enumSource ? `Allowed values: ${enumSource}.` : null,
  ].filter(Boolean)

  return parts.join('\n\n')
}

function toneForDynamicFieldState(mode?: Cbp7501DynamicFieldState['mode']) {
  switch (mode) {
    case 'required':
      return 'error'
    case 'warning':
      return 'warning'
    case 'disabled':
      return 'neutral'
    default:
      return 'info'
  }
}

function lineItemFlagDescription(flag: string) {
  switch (flag) {
    case 'SECTION_232_STEEL':
      return 'Section 232 steel reporting applies. Complete the steel-related country reporting fields.'
    case 'SECTION_232_ALUMINUM':
      return 'Section 232 aluminum reporting applies. Complete the smelt and cast country reporting fields.'
    case 'AD_CVD_POSSIBLE':
      return 'AD/CVD review may apply for this HTSUS number.'
    case 'QUOTA_OR_VISA':
      return 'Quota or visa review may be required for this line item.'
    case 'IRC_TAX_APPLICABLE':
      return 'Internal revenue tax review may be required for this line item.'
    case 'SPECIAL_PROGRAM_INDICATOR_ALLOWED':
      return 'A special program indicator may be allowed for this HTSUS number.'
    default:
      return flag.replace(/_/g, ' ')
  }
}

function formatDollarAmount(value: string | number | null | undefined) {
  if (value == null || value === '') return '$0.00'
  const numericValue =
    typeof value === 'number'
      ? value
      : Number(String(value).replace(/[^0-9.-]/g, ''))
  if (!Number.isFinite(numericValue)) return '$0.00'
  return `$${numericValue.toFixed(2)}`
}

function formatWholeDollarInput(value: string) {
  const sanitized = value.replace(/[^0-9]/g, '')
  if (!sanitized) return ''
  return String(Number.parseInt(sanitized, 10))
}

function formatHtsusNumberInput(value: string) {
  const digitsOnly = value.replace(/\D/g, '').slice(0, 10)
  if (digitsOnly.length <= 4) return digitsOnly
  if (digitsOnly.length <= 6) return `${digitsOnly.slice(0, 4)}.${digitsOnly.slice(4)}`
  return `${digitsOnly.slice(0, 4)}.${digitsOnly.slice(4, 6)}.${digitsOnly.slice(6)}`
}

function labelForDynamicFieldState(mode?: Cbp7501DynamicFieldState['mode']) {
  switch (mode) {
    case 'required':
      return 'Required by HTS'
    case 'warning':
      return 'Review for HTS'
    case 'disabled':
      return 'Not required'
    case 'optional':
      return 'Optional'
    case 'error':
      return 'Error'
    default:
      return null
  }
}

function calculateTotals(record: StructuredDocumentRecord) {
  const lineItems = record.lineItems
  const enteredValue = lineItems.reduce((total, lineItem) => total + Number(lineItem.fields.entered_value?.value || 0), 0)
  const dutyAndIrTax = lineItems.reduce((total, lineItem) => total + Number(lineItem.fields.duty_and_ir_tax?.value || 0), 0)
  const otherFees = Number(record.fields.total_other_fees?.value || 0)
  const duty = Number(record.fields.duty_total?.value || 0)
  const tax = Number(record.fields.tax_total?.value || 0)
  const other = Number(record.fields.other_total?.value || 0)

  return {
    enteredValue,
    dutyAndIrTax,
    otherFees,
    grandTotal: duty + tax + other,
  }
}

function countPopulatedFields(record: StructuredDocumentRecord, fields: DocumentFieldDefinition[]) {
  const visibleFields = fields.filter((field) => isFieldVisible(field, record.fields))
  const populatedCount = visibleFields.filter((field) => record.fields[field.key]?.value?.trim()).length
  return { populatedCount, totalCount: visibleFields.length }
}

export function CBP7501Editor({
  record,
  focusTarget,
  busy,
  saveMessage,
  onFieldChange,
  onFieldNoteChange,
  onSetFieldOverride,
  onSectionNoteChange,
  onMarkSectionComplete,
  onLineItemFieldChange,
  onAddLineItem,
  onDuplicateLineItem,
  onRemoveLineItem,
  onMoveLineItem,
  onApplyRecommendation,
  onAutoSave,
  onMarkComplete,
}: CBP7501EditorProps) {
  const definition = DOCUMENT_DEFINITIONS.cbp_7501
  const sectionStates = record.sectionStates ?? {}
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({})
  const issueMap = record.validationIssues.reduce<Record<string, string[]>>((acc, issue) => {
    acc[issue.fieldKey] = [...(acc[issue.fieldKey] ?? []), issue.message]
    return acc
  }, {})
  const recommendationMap = record.awaRecommendations.reduce<Record<string, StructuredDocumentRecord['awaRecommendations']>>((acc, recommendation) => {
    acc[recommendation.fieldKey] = [...(acc[recommendation.fieldKey] ?? []), recommendation]
    return acc
  }, {})
  const calculatedTotals = useMemo(() => calculateTotals(record), [record])
  const fieldStateEngine = useMemo(() => buildCbp7501FieldStateEngine(record), [record])
  const canCompleteDocument = useMemo(() => canMarkDocumentComplete(record), [record])
  const isDocumentComplete = record.status === 'complete'
  function toggleSection(sectionId: string, canCollapse: boolean) {
    setOpenSections((current) => {
      const definitionSection = definition.sections.find((section) => section.id === sectionId)
      const isComplete = sectionStates[sectionId]?.status === 'complete'
      const isOpen = current[sectionId] ?? (isComplete ? false : !definitionSection?.collapsedByDefault)
      if (isOpen && !canCollapse) return current
      return { ...current, [sectionId]: !isOpen }
    })
  }

  return (
    <div className="space-y-3">
      <section className="bg-white border border-neutral-200 rounded-lg shadow-sm p-4">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0">
            <SectionHeading
              eyebrow="CBP Form 7501"
              title={record.title}
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => onMarkComplete(record.id)}
              disabled={!isDocumentComplete && !canCompleteDocument}
              className={`inline-flex items-center gap-2 rounded px-4 py-2 text-sm font-semibold ${
                isDocumentComplete
                  ? 'bg-green-600 text-white'
                  : 'bg-primary text-white hover:bg-primary-dark disabled:cursor-not-allowed disabled:bg-neutral-300 disabled:text-neutral-600'
              }`}
            >
              <CheckCircle2 size={16} aria-hidden="true" />
              {isDocumentComplete ? 'Complete' : 'Mark complete'}
            </button>
          </div>
        </div>

        {fieldStateEngine && (
          <div className="mt-3 rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm text-neutral-700">
            HTS regulatory source: {fieldStateEngine.datasetManifest.revisionLabel} ({fieldStateEngine.datasetManifest.releaseDate})
          </div>
        )}

        {busy && saveMessage && <p className="mt-3 text-sm text-success">{saveMessage}</p>}
      </section>

      {definition.sections.map((section) => {
        const sectionStatus = summarizeSectionStatus(record, section.id)
        const canCompleteSection = canMarkSectionComplete(record, section.id)
        const canCollapseSection = sectionStatus === 'complete'
        const isOpen = openSections[section.id] ?? (sectionStatus === 'complete' ? false : !section.collapsedByDefault)

        return (
          <section key={section.id} className="bg-white border border-neutral-200 rounded-lg shadow-sm">
            <button
              type="button"
              onClick={() => toggleSection(section.id, canCollapseSection)}
              className="flex w-full items-start justify-between gap-4 px-5 py-4 text-left"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center gap-2">
                    <h4 className="text-base font-bold text-primary">{section.title}</h4>
                    {section.description && (
                      <InlineHelp text={section.description} title={`${section.title} help`} align="left" />
                    )}
                  </div>
                  <StatusPill label={labelForSectionStatus(sectionStatus)} tone={toneForSectionStatus(sectionStatus)} />
                  {section.kind !== 'line_items' && (
                    <SectionProgressBadge record={record} fields={section.fields} />
                  )}
                </div>
              </div>
              {isOpen ? <ChevronDown size={18} aria-hidden="true" /> : <ChevronRight size={18} aria-hidden="true" />}
            </button>

            {isOpen && (
              <div className="border-t border-neutral-200 px-5 py-5 space-y-4">
                {section.kind === 'line_items' ? (
                  <LineItemsSection
                    record={record}
                    fieldStateEngine={fieldStateEngine}
                    onLineItemFieldChange={onLineItemFieldChange}
                    onAddLineItem={onAddLineItem}
                    onDuplicateLineItem={onDuplicateLineItem}
                    onRemoveLineItem={onRemoveLineItem}
                    onMoveLineItem={onMoveLineItem}
                    issueMap={issueMap}
                    onAutoSave={onAutoSave}
                  />
                ) : (
                  <>
                    <>
                    <div className="grid gap-4 md:grid-cols-2">
                      {section.fields
                        .filter((field) => isFieldVisible(field, record.fields))
                        .filter((field) =>
                          section.id !== 'parties_identification'
                            || (!BLOCK_29_FIELD_KEYS.includes(field.key) && !BLOCK_30_FIELD_KEYS.includes(field.key)),
                        )
                        .map((field) => (
                          <FieldReviewCard
                            key={field.key}
                            record={record}
                            field={field}
                            focusTarget={focusTarget}
                            issues={issueMap[field.key] ?? []}
                            recommendations={recommendationMap[field.key] ?? []}
                            dynamicState={fieldStateEngine?.topLevelFields[field.key]}
                            onFieldChange={onFieldChange}
                            onFieldNoteChange={onFieldNoteChange}
                            onSetFieldOverride={onSetFieldOverride}
                            onApplyRecommendation={onApplyRecommendation}
                            onAutoSave={onAutoSave}
                          />
                        ))}

                      {section.id === 'parties_identification' && (
                        <AddressGroupCard
                          record={record}
                          title="Block 29 — Ultimate Consignee"
                          blockNumber="29"
                          groupId="block_29_review"
                          fields={section.fields.filter((field) => BLOCK_29_FIELD_KEYS.includes(field.key))}
                          focusTarget={focusTarget}
                          issueMap={issueMap}
                          recommendationsMap={recommendationMap}
                          fieldStateEngine={fieldStateEngine}
                          onFieldChange={onFieldChange}
                          onFieldNoteChange={onFieldNoteChange}
                          onSetFieldOverride={onSetFieldOverride}
                          onApplyRecommendation={onApplyRecommendation}
                          onSectionNoteChange={onSectionNoteChange}
                          onAutoSave={onAutoSave}
                        />
                      )}

                      {section.id === 'parties_identification' && (
                        <AddressGroupCard
                          record={record}
                          title="Block 30 — Importer of Record"
                          blockNumber="30"
                          groupId="block_30_review"
                          fields={section.fields.filter((field) => BLOCK_30_FIELD_KEYS.includes(field.key))}
                          focusTarget={focusTarget}
                          issueMap={issueMap}
                          recommendationsMap={recommendationMap}
                          fieldStateEngine={fieldStateEngine}
                          onFieldChange={onFieldChange}
                          onFieldNoteChange={onFieldNoteChange}
                          onSetFieldOverride={onSetFieldOverride}
                          onApplyRecommendation={onApplyRecommendation}
                          onSectionNoteChange={onSectionNoteChange}
                          onAutoSave={onAutoSave}
                        />
                      )}
                    </div>

                    {section.id === 'fees_duties_totals' && (
                      <TotalsReconciliationCard
                        record={record}
                        calculatedTotals={calculatedTotals}
                        onSectionNoteChange={onSectionNoteChange}
                        onAutoSave={onAutoSave}
                      />
                    )}
                    </>
                  </>
                )}

                <OptionalSectionNote
                  label="Section Review"
                  value={sectionStates[section.id]?.note ?? ''}
                  onChange={(nextValue) => onSectionNoteChange(record.id, section.id, nextValue)}
                  onBlur={() => void onAutoSave(record.id, { auto: true })}
                  placeholder="Add an optional note for this section."
                  actions={
                    <button
                      type="button"
                      onClick={() => {
                        onMarkSectionComplete(record.id, section.id)
                        void onAutoSave(record.id, { auto: true })
                        if (sectionStatus === 'complete') {
                          setOpenSections((current) => ({ ...current, [section.id]: true }))
                        } else {
                          setOpenSections((current) => ({ ...current, [section.id]: false }))
                        }
                      }}
                      disabled={sectionStatus !== 'complete' && !canCompleteSection}
                      className={`rounded px-2.5 py-1 text-xs font-medium ${
                        sectionStatus === 'complete'
                          ? 'bg-green-600 text-white'
                          : 'bg-primary text-white hover:bg-primary-dark disabled:cursor-not-allowed disabled:bg-neutral-300 disabled:text-neutral-600'
                      }`}
                    >
                      {sectionStatus === 'complete' ? 'Complete' : 'Mark as Complete'}
                    </button>
                  }
                />
              </div>
            )}
          </section>
        )
      })}
    </div>
  )
}

function SummaryValue({ label, value, icon }: { label: string; value: string; icon?: ReactNode }) {
  return (
    <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3">
      <div className="flex items-center gap-2 text-neutral-500">
        {icon}
        <div className="text-xs font-semibold uppercase tracking-wider">{label}</div>
      </div>
      <div className="mt-2 text-sm font-semibold text-primary">{value}</div>
    </div>
  )
}

function SectionProgressBadge({
  record,
  fields,
}: {
  record: StructuredDocumentRecord
  fields: DocumentFieldDefinition[]
}) {
  const { populatedCount, totalCount } = countPopulatedFields(record, fields)
  return <span className="text-xs font-medium text-neutral-500">{`${populatedCount}/${totalCount} fields`}</span>
}

const BLOCK_29_FIELD_KEYS = [
  'ultimate_consignee_name',
  'ultimate_consignee_country',
  'ultimate_consignee_address',
  'ultimate_consignee_address_line_2',
  'ultimate_consignee_city_location_estate',
  'ultimate_consignee_city_island',
  'ultimate_consignee_state_island',
  'ultimate_consignee_zip_code',
]

const BLOCK_30_FIELD_KEYS = [
  'importer_of_record',
  'importer_of_record_country',
  'importer_of_record_address',
  'importer_of_record_address_line_2',
  'importer_of_record_city_location_estate',
  'importer_of_record_city_island',
  'importer_of_record_state_island',
  'importer_of_record_zip_code',
]

function AddressGroupCard({
  record,
  title,
  blockNumber,
  groupId,
  fields,
  focusTarget,
  issueMap,
  recommendationsMap,
  fieldStateEngine,
  onFieldChange,
  onFieldNoteChange,
  onSetFieldOverride,
  onApplyRecommendation,
  onSectionNoteChange,
  onAutoSave,
}: {
  record: StructuredDocumentRecord
  title: string
  blockNumber: string
  groupId: string
  fields: DocumentFieldDefinition[]
  focusTarget: DocumentWorkspaceFocusTarget | null
  issueMap: Record<string, string[]>
  recommendationsMap: Record<string, StructuredDocumentRecord['awaRecommendations']>
  fieldStateEngine: Cbp7501FieldStateEngineResult | null
  onFieldChange: (recordId: string, fieldKey: string, nextValue: string) => void
  onFieldNoteChange: (recordId: string, fieldKey: string, note: string) => void
  onSetFieldOverride: (recordId: string, fieldKey: string, enabled: boolean, reason: string) => void
  onApplyRecommendation: (recordId: string, recommendationId: string) => void
  onSectionNoteChange: (recordId: string, sectionId: string, note: string) => void
  onAutoSave: (recordId: string, options?: { auto?: boolean }) => Promise<void>
}) {
  const groupNote = record.sectionStates[groupId]?.note ?? ''
  const [commentOpen, setCommentOpen] = useState(Boolean(groupNote.trim()))
  const groupHelpText = fields
    .map((field) =>
      [field.label, field.helpText, field.exampleValue ? `Example: ${field.exampleValue}.` : null]
        .filter(Boolean)
        .join('\n'),
    )
    .join('\n\n')

  return (
    <div className="rounded-lg border border-neutral-200 p-4 md:col-span-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {groupHelpText && <InlineHelp text={groupHelpText} title={`${title} help`} align="left" />}
            <div className="text-sm font-semibold text-primary">{title}</div>
          </div>
        </div>
        <StatusPill label={`Block ${blockNumber}`} tone="neutral" />
      </div>

      <div className="mt-3 space-y-2">
        {fields.map((field) => (
          <div key={field.key}>
            <FieldReviewCard
              record={record}
              field={field}
              focusTarget={focusTarget}
              issues={issueMap[field.key] ?? []}
              recommendations={recommendationsMap[field.key] ?? []}
              dynamicState={fieldStateEngine?.topLevelFields[field.key]}
              onFieldChange={onFieldChange}
              onFieldNoteChange={onFieldNoteChange}
              onSetFieldOverride={onSetFieldOverride}
              onApplyRecommendation={onApplyRecommendation}
              onAutoSave={onAutoSave}
              compact
            />
          </div>
        ))}
      </div>

      {commentOpen && (
        <div className="mt-3 flex justify-end">
          <textarea
            rows={2}
            value={groupNote}
            onChange={(event) => onSectionNoteChange(record.id, groupId, event.target.value)}
            onBlur={() => void onAutoSave(record.id, { auto: true })}
            placeholder="Add an optional note for this block."
            className="w-full max-w-md rounded border border-neutral-300 px-3 py-2 text-sm text-neutral-900"
          />
        </div>
      )}

      <div className="mt-3 flex justify-end">
        {commentOpen ? (
          <button
            type="button"
            onClick={() => setCommentOpen(false)}
            className="rounded border border-neutral-300 px-2.5 py-1 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-100"
          >
            Hide Comment
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setCommentOpen(true)}
            className="rounded border border-neutral-300 px-2.5 py-1 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-100"
          >
            Add Comment
          </button>
        )}
      </div>
    </div>
  )
}

function FieldReviewCard({
  record,
  field,
  focusTarget,
  issues,
  recommendations,
  dynamicState,
  onFieldChange,
  onFieldNoteChange,
  onSetFieldOverride,
  onApplyRecommendation,
  onAutoSave,
  compact = false,
}: {
  record: StructuredDocumentRecord
  field: DocumentFieldDefinition
  focusTarget: DocumentWorkspaceFocusTarget | null
  issues: string[]
  recommendations: StructuredDocumentRecord['awaRecommendations']
  dynamicState?: Cbp7501DynamicFieldState
  onFieldChange: (recordId: string, fieldKey: string, nextValue: string) => void
  onFieldNoteChange: (recordId: string, fieldKey: string, note: string) => void
  onSetFieldOverride: (recordId: string, fieldKey: string, enabled: boolean, reason: string) => void
  onApplyRecommendation: (recordId: string, recommendationId: string) => void
  onAutoSave: (recordId: string, options?: { auto?: boolean }) => Promise<void>
  compact?: boolean
}) {
  const fieldState = record.fields[field.key]
  const isFocused = focusTarget?.documentId === record.id && focusTarget.fieldKey === field.key
  const reviewLabel = labelForFieldReviewStatus(fieldState?.reviewStatus)
  const dynamicLabel = labelForDynamicFieldState(dynamicState?.mode)
  const isDisabled = dynamicState?.disabled ?? false
  const [commentOpen, setCommentOpen] = useState(Boolean(fieldState?.reviewNote?.trim()))
  const fieldHelpText = buildFieldHelpText({
    helpText: field.helpText,
    origin: fieldState?.origin,
    enumSource: field.enumSource,
    exampleValue: field.exampleValue,
  })

  return (
    <div
      className={
        compact
          ? 'space-y-1'
          : `rounded-lg border p-4 ${isFocused ? 'border-accent-sunset bg-accent-sunset/5' : 'border-neutral-200'}`
      }
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {compact ? null : (
            <div className="flex items-center gap-2">
              <div className="text-sm font-semibold text-primary">{field.label}</div>
              {fieldHelpText && <InlineHelp text={fieldHelpText} title={`${field.label} help`} align="left" />}
            </div>
          )}
        </div>
        {!compact && (
          <div className="flex flex-wrap items-center gap-2">
            {reviewLabel && fieldState?.reviewStatus !== 'agent_confirmed' && (
              <StatusPill label={reviewLabel} tone="info" />
            )}
            {dynamicLabel && <StatusPill label={dynamicLabel} tone={toneForDynamicFieldState(dynamicState?.mode)} />}
            {field.blockNumber && <StatusPill label={`Block ${field.blockNumber}`} tone="neutral" />}
          </div>
        )}
      </div>
      <FieldControl
        fieldType={field.type}
        value={fieldState?.value ?? ''}
        options={field.options}
        placeholder={field.placeholder}
        disabled={isDisabled}
        onChange={(value) => onFieldChange(record.id, field.key, value)}
        onBlur={() => void onAutoSave(record.id, { auto: true })}
      />

      {dynamicState && (
        <div className={`mt-2 rounded border p-2 text-xs ${isDisabled ? 'border-neutral-200 bg-neutral-100 text-neutral-600' : 'border-secondary/20 bg-secondary-lightest text-neutral-700'}`}>
          {dynamicState.reason}
          {dynamicState.excludedFromSubmission && fieldState?.value && (
            <span> This value will stay on the record for review history, but it will be excluded from final submission unless override stays enabled.</span>
          )}
        </div>
      )}

      {!compact && (
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-neutral-500">
          {fieldState?.sourceValue && <span>Extracted: {fieldState.sourceValue}</span>}
          {fieldState?.reviewedValue && fieldState.reviewedValue !== fieldState.value && (
            <span>Previous reviewed value: {fieldState.reviewedValue}</span>
          )}
          <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
          {commentOpen ? (
            <button
              type="button"
              onClick={() => setCommentOpen(false)}
              className="rounded border border-neutral-300 px-2.5 py-1 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-100"
            >
              Hide Comment
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setCommentOpen(true)}
              className="rounded border border-neutral-300 px-2.5 py-1 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-100"
            >
              Add Comment
            </button>
          )}
          {dynamicState?.disabled && !dynamicState.overrideEnabled && (
            <button
              type="button"
              onClick={() => {
                const reason = window.prompt(`Why should ${field.label} be enabled for CBP review?`)
                if (!reason?.trim()) return
                onSetFieldOverride(record.id, field.key, true, reason.trim())
              }}
              className="rounded border border-warning px-2.5 py-1 text-[11px] font-semibold text-warning hover:bg-warning/10"
            >
              Enable override
            </button>
          )}
          {dynamicState?.overrideEnabled && (
            <button
              type="button"
              onClick={() => onSetFieldOverride(record.id, field.key, false, '')}
              className="rounded border border-neutral-300 px-2.5 py-1 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-100"
            >
              Clear override
            </button>
          )}
          </div>
        </div>
      )}

      {fieldState?.conflictNote && (
        <div className="mt-2 rounded border border-warning/30 bg-warning/10 p-2 text-xs text-neutral-700">
          {fieldState.conflictNote}
        </div>
      )}

      {recommendations.length > 0 && (
        <div className="mt-3 space-y-2">
          {recommendations.map((recommendation) => (
            <div key={recommendation.id} className="rounded-lg border border-warning/30 bg-warning/10 p-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-primary">{recommendation.message}</div>
                  <div className="mt-1 text-xs text-neutral-600">
                    Current: {recommendation.currentValue || '—'}
                    {recommendation.recommendedValue ? ` · Recommended: ${recommendation.recommendedValue}` : ''}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusPill label={recommendation.state.replace(/_/g, ' ')} tone={toneForRecommendationState(recommendation.state)} />
                  {recommendation.recommendedValue && (
                    <button
                      type="button"
                      onClick={() => onApplyRecommendation(record.id, recommendation.id)}
                      className="rounded border border-secondary px-2.5 py-1 text-xs font-semibold text-secondary hover:bg-secondary-lightest"
                    >
                      Apply recommendation
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {issues.length > 0 && (
        <ul className="mt-2 space-y-1 text-xs text-error">
          {issues.map((message, index) => (
            <li key={`${field.key}-${index}-${message}`}>{message}</li>
          ))}
        </ul>
      )}

      {!compact && commentOpen && (
        <textarea
          rows={2}
          value={fieldState?.reviewNote ?? ''}
          onChange={(event) => onFieldNoteChange(record.id, field.key, event.target.value)}
          onBlur={() => void onAutoSave(record.id, { auto: true })}
          placeholder="Optional field review note"
          className="mt-3 w-full rounded border border-neutral-300 px-3 py-2 text-sm text-neutral-900"
        />
      )}
    </div>
  )
}

function LineItemsSection({
  record,
  fieldStateEngine,
  onLineItemFieldChange,
  onAddLineItem,
  onDuplicateLineItem,
  onRemoveLineItem,
  onMoveLineItem,
  issueMap,
  onAutoSave,
}: {
  record: StructuredDocumentRecord
  fieldStateEngine: Cbp7501FieldStateEngineResult | null
  onLineItemFieldChange: (recordId: string, lineItemId: string, fieldKey: string, nextValue: string) => void
  onAddLineItem: (recordId: string) => void
  onDuplicateLineItem: (recordId: string, lineItemId: string) => void
  onRemoveLineItem: (recordId: string, lineItemId: string) => void
  onMoveLineItem: (recordId: string, lineItemId: string, direction: 'up' | 'down') => void
  issueMap: Record<string, string[]>
  onAutoSave: (recordId: string, options?: { auto?: boolean }) => Promise<void>
}) {
  const lineItems = record.lineItems
  const automationByLineId = useMemo(
    () => Object.fromEntries(lineItems.map((lineItem) => [lineItem.id, deriveCbp7501LineAutomation(lineItem)])),
    [lineItems],
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm text-neutral-600">
        <span>Use the grid below like a case-review list. Blocks 31 through 36 are the main editable cells, and Blocks 37 through 38 auto-populate in the summary panel underneath each description row.</span>
        <InlineHelp
          text={`Line numbers are assigned automatically. Select which code and quantity cell you want to edit in each row, complete Block 36 directly in the row, then review the automated 37–38 summary directly underneath the description field.\n\nThe automated summary is derived from the selected HTS code, the current HTS regulatory source (${fieldStateEngine?.datasetManifest.revisionLabel ?? 'current revision'}${fieldStateEngine?.datasetManifest.releaseDate ? `, released ${fieldStateEngine.datasetManifest.releaseDate}` : ''}), and the row quantities.\n\nIf the HTS selection triggers quota, visa, AD/CVD, or IRC review logic, that will surface in the 37-series summary cells.`}
          title="Line item help"
          align="left"
        />
      </div>

      <div className="rounded-lg border border-neutral-200">
        <table className="w-full table-fixed border-collapse">
          <thead className="sticky top-0 z-10 bg-neutral-50">
            <tr className="border-b border-neutral-200 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
              <th className="px-2 py-3 w-[4.75rem]">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-semibold normal-case tracking-normal text-neutral-700">31.</span>
                  <InlineHelp
                    title="Block 31 help"
                    align="left"
                    text={`• Line numbers are assigned automatically.\n• Each new line item receives the next available sequence.`}
                  />
                  <span className="text-sm font-semibold normal-case tracking-normal text-neutral-700">Line No.</span>
                </div>
              </th>
              <th className="px-2 py-3 w-[7.75rem]">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-semibold normal-case tracking-normal text-neutral-700">33.</span>
                  <InlineHelp
                    title="Block 33 help"
                    align="left"
                    text={`• A. HTSUS No. is required for every line item.\n• B. AD/CVD applies should only be selected when antidumping or countervailing duties apply.\n• HTSUS No. and AD/CVD details can both apply to the same line item.`}
                  />
                  <span className="text-sm font-semibold normal-case tracking-normal text-neutral-700">Code Type</span>
                </div>
              </th>
              <th className="px-2 py-3 w-[10rem]">Code Value</th>
              <th className="px-2 py-3 w-[9.25rem]">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-semibold normal-case tracking-normal text-neutral-700">34.</span>
                  <InlineHelp
                    title="Block 34 help"
                    align="left"
                    text={`• A. Gross Weight is always required.\n• Gross Weight is always reported in kilograms (KG).\n• B. Manifest Quantity is only required when the filing scenario calls for manifest-linked quantity reporting.`}
                  />
                  <span className="text-sm font-semibold normal-case tracking-normal text-neutral-700">Qty Type</span>
                </div>
              </th>
              <th className="px-2 py-3 w-[10.75rem]">Qty Value</th>
              <th className="px-2 py-3 w-[7rem]">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-semibold normal-case tracking-normal text-neutral-700">35.</span>
                  <InlineHelp
                    title="Block 35 help"
                    align="left"
                    text={`• Net Quantity records the HTSUS reporting quantity.\n• The unit of measure follows the selected HTSUS number.`}
                  />
                  <span className="text-sm font-semibold normal-case tracking-normal text-neutral-700">Net Qty</span>
                </div>
              </th>
              <th className="px-2 py-3 w-[10.5rem]">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-semibold normal-case tracking-normal text-neutral-700">36.</span>
                  <InlineHelp
                    title="Block 36 help"
                    align="left"
                    text={`• A. Entered Value is required for every line item and must be entered in whole U.S. dollars.\n• B. Charges / CHGS are required unless Mode of Transport is 60.\n• Charges are not required for passenger, hand-carried merchandise.\n• C. Relationship is required and must be Y, N, related, or not related.`}
                  />
                  <span className="text-sm font-semibold normal-case tracking-normal text-neutral-700">Value</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200 bg-white">
          {lineItems.map((lineItem, index) => {
            const automation = automationByLineId[lineItem.id]
            const htsusValue = lineItem.fields.htsus_number?.value ?? ''
            const adCvdApplies = lineItem.fields.ad_cvd_applies?.value === 'true'
            const adCvdValue = lineItem.fields.ad_cvd_number?.value ?? ''
            const grossWeightValue = lineItem.fields.gross_weight?.value ?? ''
            const manifestQuantityValue = lineItem.fields.manifest_quantity?.value ?? ''
            const manifestQuantityUnitValue = lineItem.fields.manifest_quantity_unit?.value ?? ''
            const manifestQuantityState = fieldStateEngine?.lineItemFields[lineItem.id]?.manifest_quantity
            const manifestQuantityUnitState = fieldStateEngine?.lineItemFields[lineItem.id]?.manifest_quantity_unit
            const manifestQuantityEnabled = !(manifestQuantityState?.disabled ?? false)
            const chargesState = fieldStateEngine?.lineItemFields[lineItem.id]?.charges
            const chargesEnabled = !(chargesState?.disabled ?? false)
            const enteredValue = lineItem.fields.entered_value?.value ?? ''
            const chargesValue = lineItem.fields.charges?.value ?? ''
            const relationshipValue = lineItem.fields.relationship?.value ?? record.fields.relationship_global?.value ?? ''
            const lineIssues = Object.entries(issueMap)
              .filter(([fieldKey]) => fieldKey.endsWith(`:${lineItem.id}`))
              .flatMap(([, messages]) => messages)
            return (
              <Fragment key={lineItem.id}>
                <tr key={lineItem.id} className="align-top">
                  <td className="px-2 py-3 text-sm font-semibold text-primary">{lineItem.lineNumber}</td>
                  <td className="px-2 py-3">
                    <div className="grid gap-2">
                      <label className="flex items-center gap-2 text-sm font-semibold text-neutral-800">
                        <StaticCheckboxIndicator />
                        <span>A. HTSUS No.</span>
                      </label>
                      <label className="flex items-center gap-2 text-sm font-semibold text-neutral-800">
                        <input
                          type="checkbox"
                          checked={adCvdApplies}
                          onChange={(event) => {
                            const nextChecked = event.target.checked
                            onLineItemFieldChange(record.id, lineItem.id, 'ad_cvd_applies', nextChecked ? 'true' : 'false')
                            if (!nextChecked) {
                              onLineItemFieldChange(record.id, lineItem.id, 'ad_cvd_number', '')
                            }
                            void onAutoSave(record.id, { auto: true })
                          }}
                          className="h-4 w-4 rounded border-neutral-300 text-primary focus:ring-primary"
                        />
                        <span>B. AD/CVD applies</span>
                      </label>
                    </div>
                  </td>
                  <td className="px-2 py-3">
                    <div className="grid gap-2">
                      <input
                        type="text"
                        aria-label={`Block 33A HTSUS number for line ${lineItem.lineNumber}`}
                        value={htsusValue}
                        onChange={(event) =>
                          onLineItemFieldChange(
                            record.id,
                            lineItem.id,
                            'htsus_number',
                            formatHtsusNumberInput(event.target.value),
                          )}
                        onBlur={() => void onAutoSave(record.id, { auto: true })}
                        placeholder="4012.11.4000"
                        className="w-full rounded border border-neutral-300 px-2.5 py-2 text-sm text-neutral-900"
                      />
                      <input
                        type="text"
                        aria-label={`Block 33B AD/CVD number for line ${lineItem.lineNumber}`}
                        value={adCvdValue}
                        onChange={(event) => onLineItemFieldChange(record.id, lineItem.id, 'ad_cvd_number', event.target.value.toUpperCase())}
                        onBlur={() => void onAutoSave(record.id, { auto: true })}
                        placeholder="A-000-000-000"
                        disabled={!adCvdApplies}
                        className="w-full rounded border border-neutral-300 px-2.5 py-2 text-sm text-neutral-900 disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-400"
                      />
                    </div>
                  </td>
                  <td className="px-2 py-3">
                    <div className="grid gap-2">
                      <label className="flex items-center gap-2 text-sm font-semibold text-neutral-800">
                        <StaticCheckboxIndicator />
                        <span>Gross Weight</span>
                      </label>
                      <label className={`flex items-center gap-2 text-sm font-semibold ${manifestQuantityState?.disabled ? 'text-neutral-400' : 'text-neutral-800'}`}>
                        <input
                          type="checkbox"
                          checked={manifestQuantityEnabled}
                          onChange={(event) => {
                            onLineItemFieldChange(record.id, lineItem.id, 'manifest_quantity_available', event.target.checked ? 'true' : 'false')
                            void onAutoSave(record.id, { auto: true })
                          }}
                          aria-label={`Block 34B Manifest Quantity applies for line ${lineItem.lineNumber}`}
                          className="h-4 w-4 rounded border-neutral-300 text-primary focus:ring-primary"
                        />
                        <span>Manifest Quantity</span>
                      </label>
                    </div>
                  </td>
                  <td className="px-2 py-3">
                    <div className="grid gap-2">
                      <div className="grid grid-cols-[minmax(0,1fr)_5rem] gap-2">
                        <input
                          type="text"
                          inputMode="decimal"
                          aria-label={`Block 34A gross weight for line ${lineItem.lineNumber}`}
                          value={grossWeightValue}
                          onChange={(event) => {
                            onLineItemFieldChange(record.id, lineItem.id, 'gross_weight', event.target.value)
                            onLineItemFieldChange(record.id, lineItem.id, 'gross_weight_unit', 'kg')
                          }}
                          onBlur={() => void onAutoSave(record.id, { auto: true })}
                          placeholder="0"
                          className="w-full rounded border border-neutral-300 px-2.5 py-2 text-sm text-neutral-900"
                        />
                        <div className="flex w-full items-center justify-center rounded border border-neutral-300 bg-neutral-100 px-2.5 py-2 text-sm font-semibold text-neutral-700">
                          KG
                        </div>
                      </div>
                      <div className="grid grid-cols-[minmax(0,1fr)_5rem] gap-2">
                        <input
                          type="text"
                          inputMode="decimal"
                          aria-label={`Block 34B manifest quantity for line ${lineItem.lineNumber}`}
                          value={manifestQuantityValue}
                          onChange={(event) => onLineItemFieldChange(record.id, lineItem.id, 'manifest_quantity', event.target.value)}
                          onBlur={() => void onAutoSave(record.id, { auto: true })}
                          placeholder="0"
                          disabled={manifestQuantityState?.disabled}
                          title={manifestQuantityState?.disabled ? manifestQuantityState.reason : undefined}
                          className="w-full rounded border border-neutral-300 px-2.5 py-2 text-sm text-neutral-900 disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-400"
                        />
                        <select
                          aria-label={`Block 34B manifest quantity unit for line ${lineItem.lineNumber}`}
                          value={manifestQuantityUnitValue}
                          onChange={(event) =>
                            onLineItemFieldChange(record.id, lineItem.id, 'manifest_quantity_unit', event.target.value)
                          }
                          onBlur={() => void onAutoSave(record.id, { auto: true })}
                          disabled={manifestQuantityUnitState?.disabled}
                          title={manifestQuantityUnitState?.disabled ? manifestQuantityState.reason : undefined}
                          className="w-full rounded border border-neutral-300 px-2.5 py-2 text-sm text-neutral-900 disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-400"
                        >
                          <option value="">Unit</option>
                          {CBP_7501_MANIFEST_QUANTITY_UNIT_OPTIONS.map((option) => (
                            <option key={`manifest_quantity_unit-${option.value}`} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-3">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={automation.netQuantityValue}
                      onChange={(event) =>
                        onLineItemFieldChange(
                          record.id,
                          lineItem.id,
                          'net_quantity_htsus_units',
                          buildNetQuantityValue(event.target.value, automation.lookupRecord),
                        )}
                      onBlur={() => void onAutoSave(record.id, { auto: true })}
                      placeholder="0"
                      className="w-full rounded border border-neutral-300 px-2.5 py-2 text-sm text-neutral-900"
                    />
                    <div className="mt-1 text-[11px] text-neutral-500">{automation.netQuantityUnit}</div>
                  </td>
                  <td rowSpan={2} className="px-2 py-3 align-top">
                    <div className="grid gap-2">
                      <div className="grid grid-cols-[5.75rem_minmax(0,1fr)] items-center gap-2">
                        <div className="text-sm font-semibold text-neutral-800">
                          A. Entered Value
                        </div>
                        <div className="relative">
                          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-neutral-500">
                            $
                          </span>
                          <input
                            type="text"
                            aria-label={`Block 36A entered value for line ${lineItem.lineNumber}`}
                            inputMode="numeric"
                            value={enteredValue}
                            onChange={(event) =>
                              onLineItemFieldChange(record.id, lineItem.id, 'entered_value', event.target.value.replace(/\$/g, ''))
                            }
                            onBlur={() => {
                              onLineItemFieldChange(record.id, lineItem.id, 'entered_value', formatWholeDollarInput(enteredValue))
                              void onAutoSave(record.id, { auto: true })
                            }}
                            placeholder="36A Entered value"
                            className="w-full rounded border border-neutral-300 pl-7 pr-2.5 py-2 text-sm text-neutral-900"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-[5.75rem_minmax(0,1fr)] items-center gap-2">
                        <div className={`text-sm font-semibold ${chargesEnabled ? 'text-neutral-800' : 'text-neutral-400'}`}>
                          B. CHGS
                        </div>
                        <div className="relative">
                          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-neutral-500">
                            $
                          </span>
                          <input
                            type="text"
                            aria-label={`Block 36B charges for line ${lineItem.lineNumber}`}
                            inputMode="numeric"
                            value={chargesValue}
                            onChange={(event) =>
                              onLineItemFieldChange(record.id, lineItem.id, 'charges', event.target.value.replace(/\$/g, ''))
                            }
                            onBlur={() => {
                              onLineItemFieldChange(record.id, lineItem.id, 'charges', formatWholeDollarInput(chargesValue))
                              void onAutoSave(record.id, { auto: true })
                            }}
                            placeholder="36B Charges"
                            disabled={!chargesEnabled}
                            title={!chargesEnabled ? chargesState?.reason : undefined}
                            className="w-full rounded border border-neutral-300 pl-7 pr-2.5 py-2 text-sm text-neutral-900 disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-400"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-[5.75rem_minmax(0,1fr)] items-center gap-2">
                        <div className="text-sm font-semibold text-neutral-800">
                          C. Relationship
                        </div>
                        <select
                          aria-label={`Block 36C relationship for line ${lineItem.lineNumber}`}
                          value={relationshipValue}
                          onChange={(event) =>
                            onLineItemFieldChange(record.id, lineItem.id, 'relationship', event.target.value)
                          }
                          onBlur={() => void onAutoSave(record.id, { auto: true })}
                          className="w-full rounded border border-neutral-300 px-2.5 py-2 text-sm text-neutral-900"
                        >
                          <option value="">Select relationship</option>
                          <option value="Y">Y</option>
                          <option value="N">N</option>
                          <option value="related">related</option>
                          <option value="not related">not related</option>
                        </select>
                      </div>
                    </div>
                  </td>
                </tr>

                <tr>
                  <td className="px-2 py-2 align-top" />
                  <td colSpan={5} className="px-2 py-2 align-top">
                    <textarea
                      rows={3}
                      value={lineItem.fields.description_of_merchandise?.value ?? ''}
                      onChange={(event) =>
                        onLineItemFieldChange(record.id, lineItem.id, 'description_of_merchandise', event.target.value)
                      }
                      onBlur={() => void onAutoSave(record.id, { auto: true })}
                      placeholder="32. Description of Merchandise"
                      className="min-h-[4.75rem] w-full rounded border border-neutral-300 bg-white px-3 py-2.5 text-sm text-neutral-900"
                    />
                  </td>
                </tr>

                <tr>
                  <td colSpan={7} className="px-2 py-2 align-top">
                    <div className="rounded-lg border border-neutral-200 bg-white p-3.5">
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
                        <div className="space-y-3 text-sm text-neutral-700 lg:w-[36rem] lg:max-w-[36rem] lg:flex-none">
                          <div className="flex items-center gap-1.5 font-semibold text-primary">
                            <span>37.</span>
                            <InlineHelp
                              title="Block 37 help"
                              align="left"
                              text={`• HTSUS Rate is populated from the HTSUS classification data when available.\n• AD/CVD Rate is editable and is only used when B. AD/CVD applies is selected.\n• IRC Rate is only populated when internal revenue or excise tax applies.`}
                            />
                            <span>Rates</span>
                          </div>
                          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                            <div className="max-w-[10rem] space-y-1.5">
                              <div className="font-semibold text-primary">HTSUS Rate</div>
                              <input
                                type="text"
                                aria-label={`HTSUS rate for line ${lineItem.lineNumber}`}
                                value={automation.htsusRate || 'Pending HTS'}
                                readOnly
                                disabled
                                className="w-full rounded border border-neutral-300 bg-neutral-100 px-2.5 py-2 text-sm text-neutral-500"
                              />
                            </div>
                            <div className="max-w-[9.5rem] space-y-1.5">
                              <div className={`font-semibold ${adCvdApplies ? 'text-primary' : 'text-neutral-400'}`}>AD/CVD Rate</div>
                              <input
                                type="text"
                                aria-label={`AD/CVD rate for line ${lineItem.lineNumber}`}
                                value={lineItem.fields.ad_cvd_rate?.value ?? ''}
                                onChange={(event) =>
                                  onLineItemFieldChange(record.id, lineItem.id, 'ad_cvd_rate', event.target.value)
                                }
                                onBlur={() => void onAutoSave(record.id, { auto: true })}
                                placeholder="AD/CVD Rate"
                                disabled={!adCvdApplies}
                                className="w-full rounded border border-neutral-300 px-2.5 py-2 text-sm text-neutral-900 disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-400"
                              />
                            </div>
                            <div className="space-y-1.5 min-w-0 md:min-w-[18rem]">
                              <div className="font-semibold text-primary">IRC Rate</div>
                              <input
                                type="text"
                                aria-label={`IRC rate for line ${lineItem.lineNumber}`}
                                value={automation.ircRate || '—'}
                                readOnly
                                disabled
                                className="w-full rounded border border-neutral-300 bg-neutral-100 px-2.5 py-2 text-sm text-neutral-500"
                              />
                            </div>
                            <div className="space-y-1.5 min-w-0 md:min-w-[18rem]">
                              <div className="flex items-center gap-1.5">
                                <span className="font-semibold text-primary">VISA No.</span>
                                <InlineHelp
                                  title="VISA No. help"
                                  align="left"
                                  text="Use a VISA Number when quota, textile, or visa-based compliance rules apply to this HTSUS line item. This is a compliance identifier, not a duty rate, and it may be required in addition to the HTSUS number and any applicable rates."
                                />
                              </div>
                              <input
                                type="text"
                                aria-label={`Visa number for line ${lineItem.lineNumber}`}
                                value={lineItem.fields.visa_certificate_number?.value ?? ''}
                                onChange={(event) =>
                                  onLineItemFieldChange(record.id, lineItem.id, 'visa_certificate_number', event.target.value.toUpperCase())
                                }
                                onBlur={() => void onAutoSave(record.id, { auto: true })}
                                placeholder="VISA No. (Optional)"
                                className="w-full rounded border border-neutral-300 px-2.5 py-2 text-sm text-neutral-900"
                              />
                            </div>
                            <div className="space-y-1.5 min-w-0 xl:col-start-3 xl:min-w-[18rem]">
                              <div className="flex items-center gap-1.5">
                                <span className="font-semibold text-primary">Quota / Visa Requirement Check</span>
                                <InlineHelp
                                  title="Quota / Visa Requirement Check help"
                                  align="left"
                                  text={`• This field indicates whether the selected HTSUS code may require quota or visa-related compliance review.\n• It is auto-populated from the HTSUS-linked automation rules.\n• If no quota or visa rule applies, the field will show Not Applicable to HTSUS code.`}
                                />
                              </div>
                              <input
                                type="text"
                                aria-label={`Quota or visa requirement check for line ${lineItem.lineNumber}`}
                                value={automation.quotaOrVisaMessage || 'Not Applicable to HTSUS code'}
                                readOnly
                                disabled
                                className="w-full rounded border border-neutral-300 bg-neutral-100 px-2.5 py-2 text-sm text-neutral-500"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4 text-sm text-primary lg:ml-auto lg:min-h-[10rem] lg:w-[20rem] lg:flex-none">
                          <div className="flex items-center gap-1.5 font-semibold text-primary">
                            <span>38.</span>
                            <InlineHelp
                              title="Block 38 help"
                              align="left"
                              text={`• Duty & IR Tax is calculated from the applicable HTSUS, AD/CVD, and IRC rates.\n• VISA numbers do not replace duty or tax rates.\n• Review this amount against the current entered value and any applicable trade remedies or taxes.`}
                            />
                            <span>Duty &amp; IR Tax</span>
                          </div>
                          <div className="mt-3 flex items-end gap-1 text-primary">
                            <span className="text-4xl font-bold leading-none">$</span>
                            <span className="text-4xl font-bold leading-none">
                              {formatDollarAmount(automation.dutyAndIrTax).replace(/^\$/, '')}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>

                <tr className="bg-neutral-50">
                  <td colSpan={7} className="px-3 py-4">
                    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_auto] xl:items-end">
                      <div className="space-y-3">
                        {(automation.flags.length > 0 || automation.quotaOrVisaMessage) && (
                          <div className="rounded border border-secondary/20 bg-secondary-lightest p-3 text-sm text-neutral-700">
                            <div className="font-semibold text-primary">Line item requirements</div>
                            <ul className="mt-2 list-disc space-y-1 pl-5">
                              {automation.flags.map((flag) => (
                                <li key={`${lineItem.id}-${flag}`}>{lineItemFlagDescription(flag)}</li>
                              ))}
                              {automation.quotaOrVisaMessage && (
                                <li>{automation.quotaOrVisaMessage}</li>
                              )}
                            </ul>
                          </div>
                        )}
                        {lineIssues.length > 0 && (
                          <div className="rounded border border-warning/30 bg-warning/10 p-3 text-sm text-neutral-700">
                            <ul className="list-disc space-y-1 pl-5">
                              {lineIssues.map((issue, issueIndex) => (
                                <li key={`${lineItem.id}-issue-${issueIndex}`}>{issue}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center justify-end gap-1.5 whitespace-nowrap">
                        {lineIssues.length > 0 && (
                          <span className="rounded-full bg-warning/10 px-2 py-1 text-[11px] font-semibold text-warning">
                            {lineIssues.length} issue{lineIssues.length === 1 ? '' : 's'}
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => onMoveLineItem(record.id, lineItem.id, 'up')}
                          disabled={index === 0}
                          aria-label={`Move line ${lineItem.lineNumber} up`}
                          className="inline-flex items-center gap-1 rounded border border-neutral-300 px-1.5 py-1 text-xs font-semibold text-primary hover:border-secondary hover:bg-white disabled:opacity-50"
                        >
                          <ChevronUp size={12} aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          onClick={() => onMoveLineItem(record.id, lineItem.id, 'down')}
                          disabled={index === lineItems.length - 1}
                          aria-label={`Move line ${lineItem.lineNumber} down`}
                          className="inline-flex items-center gap-1 rounded border border-neutral-300 px-1.5 py-1 text-xs font-semibold text-primary hover:border-secondary hover:bg-white disabled:opacity-50"
                        >
                          <ChevronDown size={12} aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          onClick={() => onDuplicateLineItem(record.id, lineItem.id)}
                          className="inline-flex items-center gap-1 rounded border border-neutral-300 px-1.5 py-1 text-xs font-semibold text-primary hover:border-secondary hover:bg-white"
                        >
                          <Copy size={12} aria-hidden="true" />
                          Copy
                        </button>
                        <button
                          type="button"
                          onClick={() => onRemoveLineItem(record.id, lineItem.id)}
                          disabled={lineItems.length === 1}
                          className="inline-flex items-center gap-1 rounded border border-error px-1.5 py-1 text-xs font-semibold text-error hover:bg-white disabled:opacity-50"
                        >
                          <Trash2 size={12} aria-hidden="true" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              </Fragment>
            )
          })}
          </tbody>
        </table>
      </div>

      <div className="flex justify-start">
        <button
          type="button"
          onClick={() => onAddLineItem(record.id)}
          className="inline-flex items-center gap-2 rounded border border-secondary px-3 py-1.5 text-sm font-semibold text-secondary hover:bg-secondary-lightest"
        >
          <Plus size={14} aria-hidden="true" />
          Add line item
        </button>
      </div>
    </div>
  )
}

function TotalsReconciliationCard({
  record,
  calculatedTotals,
  onSectionNoteChange,
  onAutoSave,
}: {
  record: StructuredDocumentRecord
  calculatedTotals: ReturnType<typeof calculateTotals>
  onSectionNoteChange: (recordId: string, sectionId: string, note: string) => void
  onAutoSave: (recordId: string, options?: { auto?: boolean }) => Promise<void>
}) {
  const sectionStates = record.sectionStates ?? {}
  const enteredValueDifference = Number(record.fields.total_entered_value?.value || 0) - calculatedTotals.enteredValue
  const declaredGrandTotal = Number(record.fields.grand_total?.value || 0)
  const grandTotalDifference = declaredGrandTotal - calculatedTotals.grandTotal

  return (
    <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
      <div className="text-sm font-semibold text-primary">Totals reconciliation</div>
      <div className="mt-3 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <SummaryValue label="Calculated entered value" value={formatDollarAmount(calculatedTotals.enteredValue)} />
        <SummaryValue label="Entered Block 39" value={formatDollarAmount(Number(record.fields.total_entered_value?.value || 0))} />
        <SummaryValue label="Calculated line duty/tax" value={formatDollarAmount(calculatedTotals.dutyAndIrTax)} />
        <SummaryValue label="Calculated Block 44" value={formatDollarAmount(calculatedTotals.grandTotal)} />
      </div>
      {(Math.abs(enteredValueDifference) > 0.01 || Math.abs(grandTotalDifference) > 0.01) && (
        <div className="mt-4 rounded border border-warning/30 bg-warning/10 p-3 text-sm text-neutral-700">
          There are unresolved total mismatches. Review Block 39 and Block 44 before transmission, or record an override reason if manual totals must be preserved.
        </div>
      )}
      <textarea
        rows={2}
        value={sectionStates.fees_duties_totals?.note ?? ''}
        onChange={(event) => onSectionNoteChange(record.id, 'fees_duties_totals', event.target.value)}
        onBlur={() => void onAutoSave(record.id, { auto: true })}
        placeholder="Optional totals review note or override explanation"
        className="mt-4 w-full rounded border border-neutral-300 px-3 py-2 text-sm text-neutral-900"
      />
    </div>
  )
}
